import {
  OUTBOUND_ATTRIBUTION_CLOCK_SKEW_MS,
  OUTBOUND_ATTRIBUTION_MAX_AGE_MS,
  outboundSignupAttributionInputSchema,
} from "@degaus/shared/outbound-attribution";

import type { OutboundEmailAttribution } from "./outbound-attribution";
import { getOutboundEmailAttribution } from "./outbound-attribution";

const STORAGE_KEY = "wonda:outbound-signup-attribution";
export const OUTBOUND_ATTRIBUTION_ATTEMPT_WAIT_MS = 25_000;

export type StoredOutboundSignupAttribution = OutboundEmailAttribution & {
  capturedAt: number;
};

function removeStoredAttribution(storage: Storage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can throw in private mode or sandboxed frames.
  }
}

export function captureOutboundSignupAttribution(
  search: Partial<Record<string, unknown>>,
  storage: Storage,
  now = Date.now(),
): boolean {
  const attribution = getOutboundEmailAttribution(search);
  if (!attribution) return false;

  try {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...attribution, capturedAt: now }),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Return attribution only for the account created by this signup handoff.
 * Existing users who merely click an outbound link must never be relabelled.
 */
export function readOutboundSignupAttribution(
  storage: Storage,
  accountCreatedAt: Date,
  now = Date.now(),
): StoredOutboundSignupAttribution | null {
  let raw: string | null;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = outboundSignupAttributionInputSchema.safeParse(
      JSON.parse(raw),
    );
    if (!parsed.success) {
      removeStoredAttribution(storage);
      return null;
    }

    const { capturedAt, ...attribution } = parsed.data;
    const accountCreatedAtMs = accountCreatedAt.getTime();
    const captureIsCurrent =
      capturedAt <= now + OUTBOUND_ATTRIBUTION_CLOCK_SKEW_MS &&
      now - capturedAt <= OUTBOUND_ATTRIBUTION_MAX_AGE_MS;
    const accountBelongsToHandoff =
      accountCreatedAtMs >= capturedAt &&
      accountCreatedAtMs <= now + OUTBOUND_ATTRIBUTION_CLOCK_SKEW_MS;

    if (!captureIsCurrent || !accountBelongsToHandoff) {
      removeStoredAttribution(storage);
      return null;
    }

    return { ...attribution, capturedAt };
  } catch {
    removeStoredAttribution(storage);
    return null;
  }
}

export function clearOutboundSignupAttribution(storage: Storage): void {
  removeStoredAttribution(storage);
}

type RunOutboundSignupAttributionAttemptInput = {
  accountCreatedAt: Date;
  getStorage: () => Storage | undefined;
  record: (attribution: StoredOutboundSignupAttribution) => Promise<unknown>;
  timeoutMs?: number;
};

/**
 * Finish (or explicitly time-bound) the browser-to-server attribution handoff
 * before actionable onboarding renders. Storage acquisition itself is guarded:
 * some restricted browsers throw merely by evaluating window.localStorage.
 *
 * The server owns delivery retries and records a terminal attempt marker. The
 * client deadline exists only as a fail-open UX bound for a hung transport.
 */
export async function runOutboundSignupAttributionAttempt({
  accountCreatedAt,
  getStorage,
  record,
  timeoutMs = OUTBOUND_ATTRIBUTION_ATTEMPT_WAIT_MS,
}: RunOutboundSignupAttributionAttemptInput): Promise<void> {
  let storage: Storage | undefined;
  try {
    storage = getStorage();
  } catch {
    return;
  }
  if (!storage) return;

  const attribution = readOutboundSignupAttribution(storage, accountCreatedAt);
  if (!attribution) return;

  const delivery = Promise.resolve()
    .then(() => record(attribution))
    .then(() => {
      // Any resolved server response is terminal: successful delivery,
      // rejected stale evidence, and bounded fail-open are all consumed.
      clearOutboundSignupAttribution(storage);
    })
    .catch(() => {
      // The deadline below releases onboarding even if transport failed.
      // Keep evidence only for a future page load when the server never
      // returned a terminal result.
    });

  let timeout: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    delivery,
    new Promise<void>((resolve) => {
      timeout = setTimeout(resolve, timeoutMs);
    }),
  ]);
  if (timeout) clearTimeout(timeout);
}
